import React, { useEffect, useState } from "react";
import { authorizedGet } from "../../Config/networkWithToken";
import configuration from "../../Config/configuration";
import { config, getValueFromLocalStorage, localStorageKey } from "../../utils/helper";
import Spinner from "../../Components/Spinner/Spinner";
import Images from "../../utils/images";
import { ImCancelCircle } from "react-icons/im";
import { IoIosSearch } from "react-icons/io";
import AdvanceSearch from "../../Components/AdvanceSearch/AdvanceSearch";

const DeletedUsers = () => {
  const [deletedUsers, setDeletedUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const adminId = getValueFromLocalStorage(localStorageKey.USERDATA, "adminId");
  const [searchQuery, setSearchQuery] = useState("");
  const [showAdvanceSearch, setShowAdvanceSearch] = useState(false);
  const [filter, setFilter] = useState("all");

  const fetchDeletedUsers = async () => {
    setLoading(true);
    try {
      const res = await authorizedGet(
        `${configuration.apis.getDeletedUsers}?adminId=${adminId}`,
        config,
      );
      const responseStatus = res?.data;
      if (responseStatus?.status) {
        setDeletedUsers(responseStatus?.data);
      }
    } catch (err) {
      console.log("Error fetching data:", err);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchDeletedUsers();
  }, [adminId]);

  const handleAdvanceSearch = (searchValue) => {
    setSearchQuery(searchValue.toLowerCase());
  };

  const toggleSearchBar = () => {
    setShowAdvanceSearch((prev) => !prev);
    setSearchQuery("");
  };

  const filterDeletedUser = () => {
    const today = new Date();

    return deletedUsers?.filter((user) => {
      const deletedAt = user.deletedAt ? new Date(user.deletedAt) : null;

      if (!deletedAt || isNaN(deletedAt)) return false;

      const diffTime = Math.abs(today.getTime() - deletedAt.getTime());
      const diffDays = diffTime / (1000 * 60 * 60 * 24);

      if (filter === "today")
        return deletedAt.toDateString() === today.toDateString();
      if (filter === "week") return diffDays < 7 && diffDays >= 0;
      if (filter === "month")
        return (
          deletedAt.getMonth() === today.getMonth() &&
          deletedAt.getFullYear() === today.getFullYear()
        );
      if (filter === "year")
        return deletedAt.getFullYear() === today.getFullYear();

      return true;
    });
  };

  const filteredDeletedUser = filterDeletedUser().filter((user) =>
    (user.name || "").toLowerCase().includes(searchQuery),
  );

  return (
    <div className="p-4">
      <div className="mb-4 flex flex-wrap items-center justify-center gap-4">
        <div className="flex w-full items-center justify-center text-xs text-gray-400 md:justify-end">
          Note: The details are sorted by the date the user was deleted.
        </div>
        <h2 className="text-lg text-gray-800 sm:text-xl md:text-2xl lg:text-3xl">
          Deleted Users{" "}
          <span className="text-[20px] font-normal text-gray-500">
            ({filteredDeletedUser.length})
          </span>
        </h2>

        <div className="ml-auto flex flex-wrap items-center gap-2 sm:gap-4">
          <div className="flex items-center rounded-lg px-2 py-1">
            {!showAdvanceSearch ? (
              <IoIosSearch
                className="cursor-pointer text-xl text-gray-500 sm:text-2xl"
                onClick={toggleSearchBar}
              />
            ) : (
              <div className="flex items-center gap-2">
                <AdvanceSearch onSearchValue={handleAdvanceSearch} />
                <ImCancelCircle
                  className="cursor-pointer text-lg text-gray-500 sm:text-xl"
                  onClick={toggleSearchBar}
                />
              </div>
            )}
          </div>

          <p>Sort by:</p>
          <select
            className="rounded-md border px-2 py-1 text-sm sm:text-base"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          >
            <option value="all">All</option>
            <option value="today">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="year">This Year</option>
          </select>
        </div>
      </div>

      <div className="mb-4 flex space-x-4 border-b border-gray-300"></div>

      <div className="flex flex-wrap justify-start gap-5">
        {loading ? (
          <div className="col-span-3 flex min-h-screen items-center justify-center">
            <Spinner />
          </div>
        ) : filteredDeletedUser.length > 0 ? (
          filteredDeletedUser.map((user) => (
            <div
              key={user._id}
              className="flex w-full flex-col items-center gap-3 rounded-lg bg-white p-3 shadow-md xxs:w-[80%] sm:w-[65%] sm:flex-row md:h-[220px] md:w-[45%]"
            >
              <div className="flex w-full flex-col items-center justify-center gap-2 md:w-[25%]">
                <img
                  src={user?.gender === "male" ? Images.MEN : Images.WOMEN}
                  alt={user.name}
                  className="h-20 w-20 rounded-full border border-gray-300 object-cover"
                />
                <div className="text-center">
                  <p className="text-base font-medium">
                    {user.name?.length > 10
                      ? `${user.name.slice(0, 10)}...`
                      : user.name}
                  </p>
                </div>
              </div>
              <div className="flex w-full flex-col items-center justify-center gap-2 md:w-[65%]">
                <div className="flex w-full flex-col items-start justify-center px-2 text-left">
                  <p className="whitespace-normal break-words text-sm font-semibold text-gray-800">
                    Email:{" "}
                    <span className="font-normal">{user.primaryEmail}</span>
                  </p>
                  <p className="text-sm font-semibold text-gray-800">
                    Phone No: <span className="font-normal">{user.phone}</span>
                  </p>
                  <p className="text-sm font-semibold text-gray-800">
                    Deleted Date:{" "}
                    <span className="font-normal">
                      {user.deletedAt
                        ? new Date(user.deletedAt).toLocaleDateString("en-GB")
                        : "N/A"}
                    </span>
                  </p>
                  <p className="text-sm font-semibold text-gray-800">
                    Deleted By:{" "}
                    <span className="font-normal">{user.admin}</span>
                  </p>
                  <p className="text-sm font-semibold text-gray-800">
                    Reason:
                    <span className="max-h-[4.5rem] overflow-y-auto font-normal [-webkit-box-orient:vertical] [-webkit-line-clamp:2] [display:-webkit-box]">
                      {user.reason}
                    </span>
                  </p>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="col-span-3 flex w-full items-center justify-center">
            <p className="text-center text-xl text-gray-500">
              No deleted users found.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeletedUsers;